// Comprehensive Role-Based Permission System
// Simplified: 3 app roles + 9 clinic roles

export type AppRole = 'admin' | 'staff' | 'super_admin';
export type ClinicRole = 'ops_head' | 'quality_control_manager' | 'cluster_manager' | 'clinic_incharge' | 'accountant' | 'marketing_lead' | 'associate_dentist' | 'dental_assistant' | 'front_office';
export type StandardRole = 'owner' | 'manager' | 'doctor' | 'front_desk' | 'assistant';

export interface ModulePermission {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

// All available modules in the system
export const ALL_MODULES = [
  'patients',
  'appointments',
  'clinical_visits',
  'clinical_audit',
  'cases',
  'tasks',
  'inventory',
  'reports',
  'billing',
  'dues_tracker',
  'treatment_planning',
  'treatment_tracking',
  'vendor_invoice_management',
  'consultant_logbook',
  'assistant_logbook',
  'call_logbook',
  'radiology',
  'price_chart',
  'invisalign_tracking',
  'implant_tracking',
  'oral_surgery_tracking',
  'endo_tracking',
  'prostho_tracking',
  'perio_tracking',
  'pedo_tracking',
  'general_dentistry_tracking',
  'braces_tracking',
  'prescriptions',
  // Settings modules
  'settings_management',
  'settings_clinical',
  'settings_lab',
  'settings_radiology',
  'settings_integrations',
] as const;

export type ModuleKey = typeof ALL_MODULES[number];

export type RolePermissions = Record<ModuleKey, ModulePermission>;

export function isClinicRole(role: string | null | undefined): role is ClinicRole {
  return Boolean(role) && role in CLINIC_ROLE_LABELS;
}

export function isModuleKey(module: string): module is ModuleKey {
  return (ALL_MODULES as readonly string[]).includes(module);
}

export interface AccessCeilingRoleAssignment {
  standardRole?: string | null;
  legacyRole?: string | null;
}

const STANDARD_ROLE_CEILING_MAP: Record<StandardRole, ClinicRole | null> = {
  owner: null,
  manager: 'clinic_incharge',
  doctor: 'associate_dentist',
  front_desk: 'front_office',
  assistant: 'dental_assistant',
};

export function getClinicRoleForStandardRole(role: string | null | undefined): ClinicRole | null {
  if (!role || !(role in STANDARD_ROLE_CEILING_MAP)) return null;
  return STANDARD_ROLE_CEILING_MAP[role as StandardRole];
}

export function getClinicRolesForAccessCeiling(
  assignments: AccessCeilingRoleAssignment[]
): ClinicRole[] {
  const roles = new Set<ClinicRole>();

  for (const assignment of assignments) {
    if (assignment.legacyRole === 'accountant') {
      roles.add('accountant');
      continue;
    }

    const standardRole = getClinicRoleForStandardRole(assignment.standardRole);
    if (standardRole) {
      roles.add(standardRole);
      continue;
    }

    if (isClinicRole(assignment.legacyRole)) {
      roles.add(assignment.legacyRole);
    }
  }

  return Array.from(roles);
}

// Role descriptions for UI display
export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  admin: 'Full system access, all modules, all clinics',
  staff: 'General staff role (use clinic roles for specific access)',
  super_admin: 'Platform-level super administrator',
};

export const CLINIC_ROLE_DESCRIPTIONS: Record<ClinicRole, string> = {
  ops_head: 'Operations head – medical oversight, audit authority, clinical reports',
  quality_control_manager: 'Quality control and compliance oversight',
  cluster_manager: 'Cluster operations, financials, staff management across clinics',
  clinic_incharge: 'Clinic in-charge – runs the clinic, also a doctor',
  accountant: 'View-only access to financial modules for reconciliation',
  marketing_lead: 'Marketing campaigns, lead management',
  associate_dentist: 'Clinical work, patient care, specialty cases',
  dental_assistant: 'Clinical support, logbook, patient prep',
  front_office: 'Appointments, check-in, patient support, calls',
};

// Role display names
export const APP_ROLE_LABELS: Record<AppRole, string> = {
  admin: 'Admin',
  staff: 'Staff',
  super_admin: 'Super Admin',
};

export const CLINIC_ROLE_LABELS: Record<ClinicRole, string> = {
  ops_head: 'Ops Head',
  quality_control_manager: 'Quality Control Manager',
  cluster_manager: 'Cluster Manager',
  clinic_incharge: 'Clinic Incharge',
  accountant: 'Accountant',
  marketing_lead: 'Marketing Lead',
  associate_dentist: 'Associate Dentist',
  dental_assistant: 'Dental Assistant',
  front_office: 'Front Office',
};

// Module display names
export const MODULE_LABELS: Record<ModuleKey, string> = {
  patients: 'Patients',
  appointments: 'Appointments',
  clinical_visits: 'Clinical Visits (EMR)',
  clinical_audit: 'Clinical Audit',
  cases: 'Lab Work Cases',
  tasks: 'Tasks',
  inventory: 'Inventory',
  reports: 'Reports',
  billing: 'Patient Billing & Collections',
  dues_tracker: 'Dues Tracker',
  treatment_planning: 'Treatment Planning',
  treatment_tracking: 'Treatment Tracking',
  vendor_invoice_management: 'Expense Tracker',
  consultant_logbook: 'Consultant Logbook',
  assistant_logbook: 'Assistant Logbook',
  call_logbook: 'Call Logbook',
  radiology: 'Radiology',
  price_chart: 'Price Chart',
  invisalign_tracking: 'Aligner Cases',
  implant_tracking: 'Implant Cases',
  oral_surgery_tracking: 'Oral Surgery Cases',
  endo_tracking: 'Endo Cases',
  prostho_tracking: 'Prostho Cases',
  perio_tracking: 'Perio Cases',
  pedo_tracking: 'Pedo Cases',
  general_dentistry_tracking: 'General Dentistry Cases',
  braces_tracking: 'Braces Cases',
  prescriptions: 'Prescriptions',
  settings_management: 'Settings: Management (Users, Clinics, Roles)',
  settings_clinical: 'Settings: Clinical (Treatments, Quick Notes)',
  settings_lab: 'Settings: Lab (Providers, Assignments)',
  settings_radiology: 'Settings: Radiology (Vendors, Assignments)',
  settings_integrations: 'Settings: Integrations (Vendors, SMS)',
};

// Helper to create permission object
const perm = (view: boolean, create: boolean, edit: boolean, del: boolean): ModulePermission => ({
  can_view: view,
  can_create: create,
  can_edit: edit,
  can_delete: del,
});

const FULL = perm(true, true, true, true);
const CRUD_NO_DELETE = perm(true, true, true, false);
const VIEW_ONLY = perm(true, false, false, false);
const NONE = perm(false, false, false, false);
const VIEW_CREATE = perm(true, true, false, false);

/**
 * Default permissions for each app role (simplified to admin / staff / super_admin)
 */
export function getDefaultPermissionsForRole(appRole: AppRole): Partial<Record<ModuleKey, ModulePermission>> {
  switch (appRole) {
    case 'admin':
    case 'super_admin':
      // Full access to everything
      return {
        patients: FULL, appointments: FULL, clinical_visits: FULL, clinical_audit: FULL,
        cases: FULL, tasks: FULL, inventory: FULL, reports: FULL, billing: FULL,
        dues_tracker: FULL, treatment_planning: FULL, treatment_tracking: FULL,
        vendor_invoice_management: FULL, consultant_logbook: FULL, assistant_logbook: FULL,
        call_logbook: FULL, radiology: FULL, price_chart: FULL,
        invisalign_tracking: FULL, implant_tracking: FULL, oral_surgery_tracking: FULL,
        endo_tracking: FULL, prostho_tracking: FULL, perio_tracking: FULL,
        pedo_tracking: FULL, general_dentistry_tracking: FULL, braces_tracking: FULL,
        prescriptions: FULL, settings_management: FULL, settings_clinical: FULL,
        settings_lab: FULL, settings_radiology: FULL, settings_integrations: FULL,
      };

    case 'staff':
      // Staff is only an account class. Clinic roles define actual module access.
      return {};

    default:
      return {};
  }
}

/**
 * Get additional permissions based on clinic role
 */
export function getClinicRolePermissions(clinicRole: ClinicRole): Partial<Record<ModuleKey, ModulePermission>> {
  switch (clinicRole) {
    case 'ops_head':
      // Medical oversight, audit authority, clinical reports
      return {
        patients: perm(true, false, true, false),
        appointments: VIEW_ONLY, clinical_visits: VIEW_ONLY, clinical_audit: FULL,
        cases: VIEW_ONLY, tasks: VIEW_ONLY, inventory: NONE, reports: VIEW_ONLY,
        billing: NONE, dues_tracker: NONE, treatment_planning: VIEW_ONLY,
        treatment_tracking: VIEW_ONLY, vendor_invoice_management: NONE,
        consultant_logbook: VIEW_ONLY, assistant_logbook: VIEW_ONLY, call_logbook: NONE,
        radiology: CRUD_NO_DELETE, price_chart: VIEW_ONLY,
        invisalign_tracking: CRUD_NO_DELETE, implant_tracking: CRUD_NO_DELETE,
        oral_surgery_tracking: CRUD_NO_DELETE, endo_tracking: CRUD_NO_DELETE,
        prostho_tracking: CRUD_NO_DELETE, perio_tracking: CRUD_NO_DELETE,
        pedo_tracking: CRUD_NO_DELETE, general_dentistry_tracking: CRUD_NO_DELETE,
      };

    case 'quality_control_manager':
      return {
        patients: VIEW_ONLY, appointments: VIEW_ONLY, clinical_visits: VIEW_ONLY,
        clinical_audit: FULL, cases: VIEW_ONLY, tasks: VIEW_ONLY,
        reports: VIEW_ONLY, radiology: VIEW_ONLY, price_chart: VIEW_ONLY,
      };

    case 'cluster_manager':
      // Cluster operations, financials, staff management
      return {
        patients: CRUD_NO_DELETE, appointments: CRUD_NO_DELETE, clinical_visits: VIEW_ONLY,
        clinical_audit: NONE, cases: CRUD_NO_DELETE, tasks: FULL, inventory: FULL,
        reports: VIEW_ONLY, billing: FULL, dues_tracker: FULL, treatment_planning: VIEW_ONLY,
        treatment_tracking: VIEW_ONLY, vendor_invoice_management: FULL,
        consultant_logbook: VIEW_ONLY, assistant_logbook: CRUD_NO_DELETE,
        call_logbook: CRUD_NO_DELETE, radiology: VIEW_ONLY, price_chart: VIEW_ONLY,
        invisalign_tracking: VIEW_ONLY, implant_tracking: VIEW_ONLY,
        oral_surgery_tracking: VIEW_ONLY, endo_tracking: VIEW_ONLY,
        prostho_tracking: VIEW_ONLY, perio_tracking: VIEW_ONLY,
        pedo_tracking: VIEW_ONLY, general_dentistry_tracking: VIEW_ONLY,
      };

    case 'clinic_incharge':
      // Runs the clinic + is a doctor
      return {
        patients: CRUD_NO_DELETE, appointments: CRUD_NO_DELETE, clinical_visits: CRUD_NO_DELETE,
        clinical_audit: VIEW_CREATE, cases: CRUD_NO_DELETE, tasks: FULL, inventory: CRUD_NO_DELETE,
        reports: VIEW_ONLY, billing: CRUD_NO_DELETE, dues_tracker: CRUD_NO_DELETE,
        treatment_planning: CRUD_NO_DELETE, treatment_tracking: CRUD_NO_DELETE,
        vendor_invoice_management: VIEW_ONLY, consultant_logbook: VIEW_ONLY,
        assistant_logbook: CRUD_NO_DELETE, call_logbook: CRUD_NO_DELETE,
        radiology: CRUD_NO_DELETE, price_chart: VIEW_ONLY, prescriptions: CRUD_NO_DELETE,
        invisalign_tracking: CRUD_NO_DELETE, implant_tracking: CRUD_NO_DELETE,
        oral_surgery_tracking: CRUD_NO_DELETE, endo_tracking: CRUD_NO_DELETE,
        prostho_tracking: CRUD_NO_DELETE, perio_tracking: CRUD_NO_DELETE,
        pedo_tracking: CRUD_NO_DELETE, general_dentistry_tracking: CRUD_NO_DELETE,
        braces_tracking: CRUD_NO_DELETE,
      };

    case 'associate_dentist':
      // Clinical work, patient care, specialty cases - NO billing
      return {
        patients: CRUD_NO_DELETE, appointments: CRUD_NO_DELETE, clinical_visits: CRUD_NO_DELETE,
        clinical_audit: VIEW_CREATE, cases: CRUD_NO_DELETE, tasks: CRUD_NO_DELETE,
        inventory: NONE, reports: NONE, billing: NONE, dues_tracker: NONE,
        treatment_planning: CRUD_NO_DELETE, treatment_tracking: CRUD_NO_DELETE,
        vendor_invoice_management: NONE, consultant_logbook: NONE, assistant_logbook: NONE,
        call_logbook: NONE, radiology: CRUD_NO_DELETE, price_chart: VIEW_ONLY,
        prescriptions: CRUD_NO_DELETE,
        invisalign_tracking: CRUD_NO_DELETE, implant_tracking: CRUD_NO_DELETE,
        oral_surgery_tracking: CRUD_NO_DELETE, endo_tracking: CRUD_NO_DELETE,
        prostho_tracking: CRUD_NO_DELETE, perio_tracking: CRUD_NO_DELETE,
        pedo_tracking: CRUD_NO_DELETE, general_dentistry_tracking: CRUD_NO_DELETE,
        braces_tracking: CRUD_NO_DELETE,
      };

    case 'accountant':
      return {
        patients: NONE, appointments: NONE, clinical_visits: NONE, clinical_audit: NONE,
        cases: NONE, tasks: NONE, inventory: NONE, reports: VIEW_ONLY,
        billing: VIEW_ONLY, dues_tracker: VIEW_ONLY, treatment_planning: VIEW_ONLY,
        treatment_tracking: NONE, vendor_invoice_management: VIEW_ONLY,
        consultant_logbook: NONE, assistant_logbook: NONE, call_logbook: NONE,
        radiology: NONE, price_chart: VIEW_ONLY,
      };

    case 'marketing_lead':
      return {
        patients: VIEW_ONLY, appointments: VIEW_ONLY, call_logbook: CRUD_NO_DELETE,
        reports: VIEW_ONLY, tasks: CRUD_NO_DELETE, price_chart: VIEW_ONLY,
      };

    case 'dental_assistant':
      return {
        patients: VIEW_ONLY, appointments: VIEW_ONLY, clinical_visits: VIEW_ONLY,
        clinical_audit: NONE, cases: VIEW_ONLY, tasks: CRUD_NO_DELETE,
        inventory: FULL, reports: NONE, billing: NONE, dues_tracker: NONE,
        treatment_planning: NONE, treatment_tracking: NONE, vendor_invoice_management: NONE,
        consultant_logbook: NONE, assistant_logbook: CRUD_NO_DELETE, call_logbook: NONE,
        radiology: VIEW_ONLY, price_chart: VIEW_ONLY,
      };

    case 'front_office':
      return {
        patients: CRUD_NO_DELETE, appointments: CRUD_NO_DELETE, clinical_visits: VIEW_ONLY,
        clinical_audit: NONE, cases: VIEW_ONLY, tasks: CRUD_NO_DELETE, inventory: NONE,
        reports: NONE, billing: VIEW_CREATE, dues_tracker: VIEW_ONLY,
        treatment_planning: NONE, treatment_tracking: NONE, vendor_invoice_management: NONE,
        consultant_logbook: NONE, assistant_logbook: NONE, call_logbook: CRUD_NO_DELETE,
        radiology: VIEW_CREATE, price_chart: VIEW_ONLY, prescriptions: VIEW_ONLY,
      };

    default:
      return {};
  }
}

/**
 * Convert permission map to array format for database insertion
 */
export function permissionsToArray(
  userId: string,
  permissions: Partial<Record<ModuleKey, ModulePermission>>
): Array<{
  user_id: string;
  module: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}> {
  return Object.entries(permissions)
    .filter(([_, perm]) => perm && perm.can_view)
    .map(([module, perm]) => ({
      user_id: userId,
      module,
      can_view: perm!.can_view,
      can_create: perm!.can_create,
      can_edit: perm!.can_edit,
      can_delete: perm!.can_delete,
    }));
}

/**
 * Merge app role permissions with clinic role permissions
 * Clinic role can enhance but not restrict app role permissions
 */
export function mergePermissions(
  appRolePerms: Partial<Record<ModuleKey, ModulePermission>>,
  clinicRolePerms: Partial<Record<ModuleKey, ModulePermission>>
): Partial<Record<ModuleKey, ModulePermission>> {
  const merged: Partial<Record<ModuleKey, ModulePermission>> = { ...appRolePerms };

  for (const [module, clinicPerm] of Object.entries(clinicRolePerms)) {
    const key = module as ModuleKey;
    const appPerm = merged[key];

    if (!appPerm) {
      merged[key] = clinicPerm;
    } else if (clinicPerm) {
      merged[key] = {
        can_view: appPerm.can_view || clinicPerm.can_view,
        can_create: appPerm.can_create || clinicPerm.can_create,
        can_edit: appPerm.can_edit || clinicPerm.can_edit,
        can_delete: appPerm.can_delete || clinicPerm.can_delete,
      };
    }
  }

  return merged;
}

export function applyPermissionCeiling(
  granted?: ModulePermission,
  ceiling?: ModulePermission
): ModulePermission {
  return {
    can_view: Boolean(granted?.can_view && ceiling?.can_view),
    can_create: Boolean(granted?.can_create && ceiling?.can_create),
    can_edit: Boolean(granted?.can_edit && ceiling?.can_edit),
    can_delete: Boolean(granted?.can_delete && ceiling?.can_delete),
  };
}

/**
 * Get the effective permissions for a user based on app role and clinic roles
 */
export function getEffectivePermissions(
  appRole: AppRole,
  clinicRoles: ClinicRole[]
): Partial<Record<ModuleKey, ModulePermission>> {
  let permissions = getDefaultPermissionsForRole(appRole);

  for (const clinicRole of clinicRoles) {
    const clinicPerms = getClinicRolePermissions(clinicRole);
    permissions = mergePermissions(permissions, clinicPerms);
  }

  return permissions;
}
